<template>
  <div>
    <!-- Competência corrente -->
    <b-card
      v-if="currentCompetence"
      no-body
      class="mb-1"
    >
      <b-card-body class="d-flex align-items-center flex-wrap">
        <b-avatar
          variant="light-primary"
          rounded
        >
          <feather-icon
            icon="CalendarIcon"
            size="18"
          />
        </b-avatar>
        <div class="ml-1">
          <h5 class="mb-0">
            {{ currentCompetenceLabel }}
          </h5>
          <small class="text-muted">Competência em aberto</small>
        </div>
        <div class="ml-2">
          <h5 class="mb-0">
            {{ currentCompetence.closes_at | onlyDate }}
          </h5>
          <small class="text-muted">
            Fechamento previsto ({{ closingBusinessDayLabel }})
          </small>
        </div>
      </b-card-body>
    </b-card>

    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="mb-0">Exibir</label>
            <v-select
              v-model="itemsPerPage"
              dir="ltr"
              :options="itemsPerPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
              @input="search(currentPage)"
            >
              <template #no-options>
                Sem resultados
              </template>
            </v-select>
            <label class="mb-0">itens</label>
          </b-col>
        </b-row>
      </div>
      <b-table
        :items="items"
        responsive
        :fields="tableColumns"
        primary-key="competence"
        show-empty
        empty-text="Não foram encontradas competências para esta busca"
        :busy="loading"
      >
        <!-- Column: competência -->
        <template #cell(competence)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{ formatCompetence(data.item.competence) }}</span>
          </div>
        </template>
        <!-- Column: status -->
        <template #cell(status)="data">
          <b-badge :variant="isClosed(data.item) ? 'light-secondary' : 'light-success'">
            {{ isClosed(data.item) ? "Fechada" : "Aberta" }}
          </b-badge>
        </template>
        <!-- Column: data prevista de fechamento -->
        <template #cell(expected_closing_date)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{ expectedClosingDate(data.item) | onlyDate }}</span>
          </div>
        </template>
        <!-- Column: data efetiva de fechamento -->
        <template #cell(closed_at)="data">
          <div class="text-wrap">
            <span
              v-if="data.item.closed_at"
              class="align-text-top"
            >{{ data.item.closed_at | dateTime }}</span>
            <span
              v-else
              class="align-text-top text-muted"
            >Não fechada</span>
          </div>
        </template>
        <!-- Column: responsável -->
        <template #cell(closed_by)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{ closedByName(data.item) }}</span>
          </div>
        </template>
        <!-- Column: quantidade de contratos -->
        <template #cell(contracts_count)="data">
          <div class="text-wrap text-center">
            <span class="align-text-top">{{ data.item.contracts_count || 0 }}</span>
          </div>
        </template>
        <!-- Column: quantidade de alterações extraordinárias -->
        <template #cell(exceptions_count)="data">
          <div class="text-wrap text-center">
            <b-link
              v-if="data.item.exceptions_count"
              @click="goToExceptions(data.item)"
            >
              {{ data.item.exceptions_count }}
            </b-link>
            <span
              v-else
              class="align-text-top"
            >0</span>
          </div>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              Exibindo {{ pageItemsFrom }} à {{ pageItemsTo }} de {{ totalItems }} itens
            </span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              :value="currentPage"
              :total-rows="totalItems"
              :per-page="itemsPerPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
              @change="search"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BTable,
  BBadge,
  BLink,
  BAvatar,
  BPagination,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { mapActions, mapGetters } from 'vuex'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { apiErrorToast } from '@/helpers/error_messages'
import { competenceLabel } from '@/modules/contract/utils/contractLock'
import * as sharedTypes from '@/modules/shared/store/types'
import * as types from '../store/types'

export default {
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BTable,
    BBadge,
    BLink,
    BAvatar,
    BPagination,
    vSelect,
  },
  setup() {
    return { toast: useToast() }
  },
  data() {
    return {
      loading: false,
      tableColumns: [
        { key: 'competence', label: 'Competência' },
        { key: 'status', label: 'Status' },
        { key: 'expected_closing_date', label: 'Data prevista de fechamento' },
        { key: 'closed_at', label: 'Data efetiva de fechamento' },
        { key: 'closed_by', label: 'Responsável' },
        { key: 'contracts_count', label: 'Contratos' },
        { key: 'exceptions_count', label: 'Alterações extraordinárias' },
      ],
      currentPage: 1,
      itemsPerPage: 1,
      // Datas previstas buscadas sob demanda para as competências que o ledger ainda
      // não calculou (normalmente só a competência em aberto).
      expectedDates: {},
    }
  },
  computed: {
    ...mapGetters({
      competencesSearchResult: types.COMPETENCES_SEARCH_RESULT,
      currentCompetence: types.CURRENT_COMPETENCE,
      initialItemsPerPage: sharedTypes.INITIAL_ITEMS_PER_PAGE,
      itemsPerPageOptions: sharedTypes.ITEMS_PER_PAGE_OPTIONS,
    }),
    items() {
      return this.competencesSearchResult?.data || []
    },
    totalItems() {
      return this.competencesSearchResult?.total || 0
    },
    pageItemsFrom() {
      return this.competencesSearchResult?.from || 0
    },
    pageItemsTo() {
      return this.competencesSearchResult?.to || 0
    },
    currentCompetenceLabel() {
      return competenceLabel(this.currentCompetence?.competence) || 'Competência não identificada'
    },
    closingBusinessDayLabel() {
      const businessDay = this.currentCompetence?.closing_business_day

      return businessDay ? `${businessDay}º dia útil` : 'dia útil de fechamento'
    },
  },
  mounted() {
    this.itemsPerPage = this.initialItemsPerPage
    this.getCurrentCompetence()
      .catch(error => {
        this.toast({
          component: ToastificationContent,
          props: apiErrorToast(
            error,
            'Ocorreu um erro ao carregar a competência atual. Entre em contato com o setor de TI.',
          ),
        })
      })
    this.search()
  },
  methods: {
    ...mapActions({
      searchCompetences: types.SEARCH_COMPETENCES,
      getCurrentCompetence: types.GET_CURRENT_COMPETENCE,
      getExpectedClosingDate: types.GET_EXPECTED_CLOSING_DATE,
    }),
    formatCompetence(competence) {
      return competenceLabel(competence) || 'Não informada'
    },
    isClosed(item) {
      if (typeof item.is_closed === 'boolean') return item.is_closed
      if (typeof item.is_competence_closed === 'boolean') return item.is_competence_closed

      return Boolean(item.closed_at)
    },
    closedByName(item) {
      if (typeof item.closed_by === 'string' && item.closed_by) return item.closed_by

      return item.closed_by?.name || 'Sistema'
    },
    expectedClosingDate(item) {
      return item.expected_closing_date || this.expectedDates[item.competence]
    },
    // A competência ainda em aberto costuma vir sem data prevista: consultamos o
    // endpoint que aplica a regra do 3º dia útil.
    fillMissingExpectedDates() {
      this.items
        .filter(item => item.competence
          && !item.expected_closing_date
          && !this.expectedDates[item.competence])
        .forEach(item => {
          this.getExpectedClosingDate({ competence: item.competence })
            .then(response => {
              this.$set(this.expectedDates, item.competence, response.data?.expected_closing_date)
            })
            .catch(() => {
              // Silencioso de propósito: a coluna apenas continua sem data prevista.
            })
        })
    },
    search(currentPage) {
      this.loading = true
      this.currentPage = Number.isInteger(currentPage) ? currentPage : 1
      this.searchCompetences({
        limit: this.itemsPerPage,
        currentPage: this.currentPage,
      })
        .then(() => {
          this.fillMissingExpectedDates()
        })
        .catch(error => {
          this.toast({
            component: ToastificationContent,
            props: apiErrorToast(
              error,
              'Ocorreu um erro ao buscar as competências. Entre em contato com o setor de TI.',
            ),
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    goToExceptions(item) {
      this.$router.push({
        name: 'contract-closing-exceptions-report',
        query: { competence: item.competence },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
